import {
  Actions,
  Box,
  Button,
  ButtonLink,
  Dialog,
  IconChevron,
  IconNewWindow,
  Stack,
  Strong,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import {
  type FC,
  type ComponentProps,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { isInternalUrl, replaceTextWithLinks } from './urlUtils';

interface TextDisclosureWithLinksProps {
  text: string;
  linesToPreview: number;
  size: ComponentProps<typeof Text>['size'];
}

export const TextDisclosureWithLinks: FC<TextDisclosureWithLinksProps> = ({
  text,
  size,
  linesToPreview,
}) => {
  const { t } = useTranslations(translations);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [externalUrlClicked, setExternalUrlClicked] = useState('');

  const fullTextRef = useRef<HTMLDivElement>(null);
  const visibleTextRef = useRef<HTMLDivElement>(null);
  const [showFullText, setShowFullText] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);

  // This is copied from https://github.com/SEEK-Jobs/metropolis/blob/87cc490d72b2bc930f0b4c7c0c16fb86ea6613e9/packages/profile-read-ui/src/components/ReadMore/ReadMore.tsx#L41-L75
  useEffect(() => {
    const calculateIfTextTooLong = (
      fullEl: HTMLDivElement,
      visibleTextEl: HTMLDivElement,
    ) => {
      const fullTextHeight = fullEl.scrollHeight;
      const visibleTextHeight = visibleTextEl.scrollHeight;

      const isTooLong = fullTextHeight > visibleTextHeight;
      setShowButton(isTooLong);
    };

    if (fullTextRef.current && visibleTextRef.current) {
      calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);

      // Re-measure when web fonts have loaded
      document.fonts?.ready.then(() => {
        if (fullTextRef.current && visibleTextRef.current) {
          calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);
        }
      });
    }
  }, [showButton]);

  const handleShowMore = useCallback(() => {
    setShowFullText(!showFullText);
  }, [showFullText]);

  const onClickUrlFn = (url: string) => {
    if (isInternalUrl(url)) {
      window.open(url, '_blank', 'noopener');
    } else {
      setExternalUrlClicked(url);
      setIsDialogOpen(true);
    }
  };

  const replacedText = replaceTextWithLinks(text, onClickUrlFn);

  const onClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      <Stack space="small">
        <Box position="relative">
          <Box
            ref={fullTextRef}
            position="absolute"
            inset={0}
            pointerEvents="none"
            overflow="hidden"
            opacity={0}
          >
            <Text size={size}>{replacedText}</Text>
          </Box>
          <Box ref={visibleTextRef} aria-hidden>
            <Text
              size={size}
              maxLines={showFullText ? undefined : linesToPreview}
            >
              {replacedText}
            </Text>
          </Box>
        </Box>
        {showButton && (
          <Text size={size}>
            <TextLinkButton
              onClick={handleShowMore}
              hitArea="large"
              weight="weak"
              icon={
                <IconChevron
                  direction={showFullText ? 'up' : 'down'}
                  alignY="lowercase"
                />
              }
              iconPosition="trailing"
            >
              {showFullText ? t('Less') : t('More')}
            </TextLinkButton>
          </Text>
        )}
      </Stack>
      <Dialog
        id="externalUrlDialog"
        width="small"
        onClose={onClose}
        closeLabel={t('Close')}
        open={isDialogOpen}
        title={t('Are you sure you want to leave SEEK?')}
      >
        <Stack space="large">
          <Text>
            {t(
              'The link (<Strong>{externalUrl}</Strong>) will take you to a site outside of SEEK. <br></br> We recommend you only click on links for URLs you recognise.',
              {
                externalUrl: externalUrlClicked,
                Strong: (v) => <Strong key="confirmLeavingMessage">{v}</Strong>,
                br: () => <br key="confirmLeavingMessageLineBreak" />,
              },
            )}
          </Text>
          <Actions>
            <Button onClick={onClose} tone="formAccent">
              {t('Return')}
            </Button>
            <ButtonLink
              href={externalUrlClicked}
              onClick={onClose}
              target="_blank"
              rel="noopener"
              role="button"
              tone="formAccent"
              variant="ghost"
            >
              {t('Go to site')}&nbsp;
              <IconNewWindow
                alignY="lowercase"
                title={t('Opens in a new tab')}
                titleId="externalLinkIcon"
              />
            </ButtonLink>
          </Actions>
        </Stack>
      </Dialog>
    </>
  );
};
